'use client';

import { type ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, Outlet, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';
import { Alert, LoadingState, Sidebar, Stack, Typography, notify } from '@org/design-system';
import {
  AuthGuard,
  CustomPage,
  LoginPage,
  LogoutPage,
  getLoginPath,
  getLogoutPath,
  isAuthEnabled,
  isExternalAuthUrl,
  resolveIcon,
  toRoutePath,
  type AppConfig,
  useRuntimeAuth,
} from '@org/runtime-engine';
import { SYNC_QUEUE_EVENT, syncQueue, type SyncQueueItem } from './shadcn-renderer/offline/SyncQueue';
import { PresenceProvider } from './shadcn-renderer/collaboration/PresenceProvider';
import { ActiveUsersStack } from './shadcn-renderer/collaboration/ActiveUsersStack';
import { hasRequiredRole, normalizeRuntimeRole, resolveUserIdFromToken } from './shadcn-renderer/auth/roles';
import { SlowQueryIndicator } from './shadcn-renderer/observability/QueryPerformanceObserver';
import { emitUsageEvent } from './lib/usage-tracker';
import { useCollaborationIdentity } from './shadcn-renderer/collaboration/PresenceProvider';
import { PageRenderer as ShadcnPageRenderer } from './shadcn-renderer/PageRenderer';
import type { ShadcnPageConfig } from './shadcn-renderer/types';
import { loadRuntimePlugins } from './shadcn-renderer/plugin-runtime';

interface ShadcnAppShellProps {
  config: AppConfig;
}

function SidebarProvider({ children }: { children: ReactNode }) {
  return <div className="min-h-screen bg-background text-foreground">{children}</div>;
}

function PageRenderer({ config, routePath }: { config: AppConfig; routePath: string }) {
  const page = config.pages.find(item => item.path === routePath);

  if (!page) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center">
        <Typography color="muted">Page not found.</Typography>
      </div>
    );
  }

  const pageId = page.id ?? routePath;

  return (
    <PresenceProvider appId={config.id} pageId={pageId}>
      <CollaborationUsageHeartbeat appId={config.id} />
      <div className="space-y-4">
        <div className="flex items-center justify-end">
          <ActiveUsersStack />
        </div>
        {'layout' in page ? (
          <ShadcnPageRenderer
            page={page as ShadcnPageConfig}
            apiBaseUrl={config.apiBaseUrl}
            plan={config.plan}
            billingPortalUrl={config.billing?.customerPortalUrl}
          />
        ) : (
          <CustomPage pageConfig={page} apiBaseUrl={config.apiBaseUrl} />
        )}
      </div>
    </PresenceProvider>
  );
}

function BuilderRouteGuard({ config, children }: { config: AppConfig; children: ReactNode }) {
  const { token, role } = useRuntimeAuth();
  const location = useLocation();

  if (!location.pathname.startsWith('/builder')) {
    return <>{children}</>;
  }

  const userId = resolveUserIdFromToken(token, config.auth);
  const mappedRole = userId ? config.roles?.[userId] : undefined;
  const effectiveRole = normalizeRuntimeRole(mappedRole ?? role);

  if (hasRequiredRole(effectiveRole, 'admin')) {
    return <>{children}</>;
  }

  const fallbackPath = config.pages[0]?.path ?? '/';
  return <Navigate to={fallbackPath} replace />;
}

function CollaborationUsageHeartbeat({ appId }: { appId: string }) {
  const { enabled } = useCollaborationIdentity();

  useEffect(() => {
    if (!enabled) {
      return;
    }

    const emitHeartbeat = () => {
      void emitUsageEvent({
        appId,
        event: 'collaborator_active',
        quantity: 1,
      });
    };

    emitHeartbeat();
    const intervalId = window.setInterval(emitHeartbeat, 60_000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [appId, enabled]);

  return null;
}

function mutationLabel(item: SyncQueueItem): string {
  if (item.mutationType === 'create') {
    return 'Created queued record';
  }

  if (item.mutationType === 'update') {
    return 'Updated queued record';
  }

  return 'Deleted queued record';
}

function ShadcnLayout({ config }: ShadcnAppShellProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isOnline, setIsOnline] = useState(() => window.navigator.onLine);
  const [queuedCount, setQueuedCount] = useState(0);

  const updateQueueCount = useCallback(async () => {
    const count = await syncQueue.count();
    setQueuedCount(count);
  }, []);

  const drainQueue = useCallback(async () => {
    const result = await syncQueue.drain({
      queryClient,
      onResolved: (item) => {
        notify.success(mutationLabel(item), `\${item.entityPath} synced successfully.`);
      },
    });

    if (result.failed > 0) {
      notify.info('Some queued changes could not be synced yet.');
    }

    await updateQueueCount();
  }, [queryClient, updateQueueCount]);

  useEffect(() => {
    void updateQueueCount();

    const onQueueChange = () => {
      void updateQueueCount();
    };

    const onOnline = () => {
      setIsOnline(true);
      void drainQueue();
    };

    const onOffline = () => {
      setIsOnline(false);
    };

    window.addEventListener(SYNC_QUEUE_EVENT, onQueueChange);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);

    return () => {
      window.removeEventListener(SYNC_QUEUE_EVENT, onQueueChange);
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, [drainQueue, updateQueueCount]);

  useEffect(() => {
    void emitUsageEvent({
      appId: config.id,
      event: 'page_view',
      quantity: 1,
    });
  }, [config.id, location.pathname]);

  const items = useMemo(() => config.pages.map(page => ({
    id: page.id,
    label: page.title,
    icon: page.icon ? resolveIcon(page.icon) : undefined,
    isActive: location.pathname === page.path,
    onClick: () => navigate(page.path),
  })), [config.pages, location.pathname, navigate]);

  return (
    <SidebarProvider>
      <div className="flex min-h-screen">
        <Sidebar
          items={items}
          header={(
            <Stack gap={1} className="px-2 py-1">
              <Typography variant="h4">{config.name}</Typography>
              <Typography variant="small" color="muted">Shadcn shell</Typography>
            </Stack>
          )}
        />
        <main className="flex-1 p-6">
          {!isOnline ? (
            <Alert
              variant="warning"
              title="You are offline"
              description={`\${queuedCount} queued mutation\${queuedCount === 1 ? '' : 's'} pending sync.`}
              className="mb-4"
            />
          ) : null}
          <Outlet />
          <SlowQueryIndicator />
        </main>
      </div>
    </SidebarProvider>
  );
}

export function ShadcnAppShell({ config }: ShadcnAppShellProps) {
  const [pluginsReady, setPluginsReady] = useState(false);
  const [pluginError, setPluginError] = useState<Error | null>(null);

  useEffect(() => {
    setPluginsReady(false);
    setPluginError(null);

    loadRuntimePlugins(config.plugins as unknown[] | undefined)
      .then(() => setPluginsReady(true))
      .catch(error => {
        setPluginError(error instanceof Error ? error : new Error('Plugin load failed'));
      });
  }, [config.plugins]);

  useEffect(() => {
    const tenantPlan = (config as AppConfig & { plan?: string }).plan ?? config.billing?.provider ?? 'unknown';
    Sentry.setTag('appId', config.id);
    Sentry.setTag('plan', tenantPlan);
  }, [config]);
  if (pluginError) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Typography color="destructive">{pluginError.message}</Typography>
      </div>
    );
  }

  if (!pluginsReady) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <LoadingState message="Loading plugins..." />
      </div>
    );
  }

  if (!config.pages.length) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Typography color="muted">No pages configured.</Typography>
      </div>
    );
  }

  const defaultPath = config.pages[0]?.path ?? '/';
  const authEnabled = isAuthEnabled(config.auth);
  const loginPath = getLoginPath(config.auth);
  const logoutPath = getLogoutPath(config.auth);

  const rootPage = config.pages.find(p => p.path === '/' || p.path === '');
  const nonRootPages = config.pages.filter(p => p.path !== '/' && p.path !== '');

  const routedPages = (
    <>
      {rootPage ? (
        <Route
          index
          element={<BuilderRouteGuard config={config}><PageRenderer config={config} routePath={rootPage.path} /></BuilderRouteGuard>}
        />
      ) : (
        <Route index element={<Navigate to={defaultPath} replace />} />
      )}
      {nonRootPages.map(page => (
        <Route
          key={page.id}
          path={page.path.replace(/^\//, '')}
          element={<BuilderRouteGuard config={config}><PageRenderer config={config} routePath={page.path} /></BuilderRouteGuard>}
        />
      ))}
      <Route path="builder/*" element={<BuilderRouteGuard config={config}><Navigate to={defaultPath} replace /></BuilderRouteGuard>} />
      <Route path="*" element={<Navigate to={defaultPath} replace />} />
    </>
  );

  return (
    <Routes>
      {authEnabled && !isExternalAuthUrl(loginPath) ? (
        <Route path={toRoutePath(loginPath)} element={<LoginPage auth={config.auth} />} />
      ) : null}
      {authEnabled && !isExternalAuthUrl(logoutPath) ? (
        <Route path={toRoutePath(logoutPath)} element={<LogoutPage auth={config.auth} />} />
      ) : null}
      <Route path="/" element={<ShadcnLayout config={config} />}>
        {authEnabled ? <Route element={<AuthGuard auth={config.auth} />}>{routedPages}</Route> : routedPages}
      </Route>
    </Routes>
  );
}
